import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, MapPin, Clock, Zap, Flame, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { logger } from '@/lib/logger';

interface RunData {
  id: string;
  distance_km: number;
  duration_seconds: number;
  pace_avg: number;
  calories: number;
  emotion?: string;
  map_image_url?: string;
  created_at: string;
  profiles: {
    name: string;
    avatar_url?: string;
  };
}

export default function ShareRun() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<RunData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (runId) {
      loadRunData();
      trackView();
    }
  }, [runId]);

  const loadRunData = async () => {
    try {
      const { data, error } = await supabase
        .from('runs')
        .select(`
          id,
          distance_km,
          duration_seconds,
          pace_avg,
          calories,
          emotion,
          map_image_url,
          created_at,
          user_id
        `)
        .eq('id', runId)
        .single();

      if (error) throw error;
      
      // Buscar dados do perfil separadamente
      if (data) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('name, avatar_url')
          .eq('id', data.user_id)
          .single();
        
        setRun({
          ...data,
          profiles: profileData || { name: 'Corredor YBR', avatar_url: undefined }
        } as RunData);
      }
    } catch (error) {
      logger.error('Error loading shared run', error, { runId });
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    logger.info('Shared run view tracked', { runId });
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const EMOTION_EMOJIS: Record<string, string> = {
    'happy': '😊',
    'calm': '😌',
    'neutral': '😐',
    'tired': '😓',
    'sad': '😔'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-64 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="text-6xl">😕</div>
            <h2 className="text-2xl font-bold">Corrida não encontrada</h2>
            <p className="text-muted-foreground">
              Esta corrida pode ter sido removida ou o link está incorreto.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              Ir para o App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
      {/* Header */}
      <div className="bg-background border-b sticky top-0 z-10 backdrop-blur-sm bg-background/80">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/ybr-logo-3.jpg" alt="YBR" className="h-8 w-8 rounded" />
            <span className="font-bold text-lg">YBR Running</span>
          </div>
          <Button onClick={() => navigate('/')} variant="default" size="sm">
            Baixar App
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Perfil do Usuário */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-primary">
                <AvatarImage src={run.profiles.avatar_url} />
                <AvatarFallback className="text-lg">
                  {run.profiles.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{run.profiles.name}</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(run.created_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </div>
              </div>
              {run.emotion && (
                <div className="text-4xl">
                  {EMOTION_EMOJIS[run.emotion] || '🏃'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mapa do Percurso */}
        {run.map_image_url && (
          <Card className="overflow-hidden">
            <img 
              src={run.map_image_url} 
              alt="Mapa do percurso"
              className="w-full h-auto"
            />
          </Card>
        )}

        {/* Métricas da Corrida */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Estatísticas da Corrida
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-primary/5 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-primary">
                  {run.distance_km.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Quilômetros</div>
              </div>
              
              <div className="bg-primary/5 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Clock className="w-5 h-5 text-primary" />
                  <div className="text-3xl font-bold text-primary">
                    {formatTime(run.duration_seconds)}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">Tempo</div>
              </div>
              
              <div className="bg-primary/5 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap className="w-5 h-5 text-primary" />
                  <div className="text-3xl font-bold text-primary">
                    {run.pace_avg.toFixed(2)}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">Pace (min/km)</div>
              </div>
              
              <div className="bg-primary/5 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Flame className="w-5 h-5 text-primary" />
                  <div className="text-3xl font-bold text-primary">
                    {run.calories}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">Calorias</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA para baixar o app */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="text-5xl">🏃‍♂️</div>
            <h3 className="text-2xl font-bold">Comece a Correr com o YBR!</h3>
            <p className="text-primary-foreground/90">
              Rastreie suas corridas, conecte-se com outros corredores e supere seus limites.
            </p>
            <Button 
              size="lg" 
              variant="secondary" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              <Download className="w-5 h-5 mr-2" />
              Baixar YBR Running
            </Button>
            <p className="text-sm text-primary-foreground/70">
              Gratuito • iOS & Android • Web
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-8">
          <p>© 2025 YBR Running. Todos os direitos reservados.</p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-primary">Privacidade</a>
            <span>•</span>
            <a href="/terms" className="hover:text-primary">Termos</a>
            <span>•</span>
            <a href="mailto:contato@ybr.app" className="hover:text-primary">Contato</a>
          </div>
        </div>
      </div>
    </div>
  );
}